import React, { memo, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import queryString from 'query-string';

import icBack from '../../../assets/imgs/ic_btn_back.svg?react';
import CenterLoading from '../../../components/CenterLoading/CenterLoading';
import { showNotificationDialog } from '../../../utils';
import SpotIdentificationCodeModal from './SpotIdentificationCodeModal';
import styles from './styles.module.css';
import { APP_ID, OTP_INPUT_AMOUNT } from '../../../utils/Constants';
import useR05Api from '../../../api/useR05Api';
import QrCodeScan from './QrCodeScan/QrCodeScan';
import CustomAntButton from '../../../components/CustomAntButton/CustomAntButton';

/**
 * コード入力のカメラ画面
 */
const CameraCodeScreen = memo(() => {
    const [isCodeInputModalOpen, setIsCodeInputModalOpen] = useState(false);
    const [isCameraOpen, setIsCameraOpen] = useState(true);
    const [qrCodeData, setQrCodeData] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const { orderTicket } = location.state ?? {};
    const { validateSpot } = useR05Api();

    // スポットコードの認証を確認
    const confirmSpotCodeAuthentication = useCallback(
        async (spotCode?: string | null) => {
            if (!spotCode) {
                return;
            }
            try {
                setIsCodeInputModalOpen(false);
                setLoading(true);
                const response = await validateSpot({
                    orderTicketId: orderTicket.orderTicketId,
                    spotCode: spotCode,
                });
                if (!response) {
                    throw new Error('Failed');
                }
                setLoading(false);
                navigate('/bought/spot-number', {
                    state: { orderTicket: orderTicket, spotInfo: response },
                });
            } catch {
                setIsCameraOpen(true);
                setLoading(false);
                setQrCodeData(null);
                showNotificationDialog('error', '', t('notification.title'), t('error-message.spot-code.wrong-code'));
            }
        },
        [navigate, orderTicket, t, validateSpot],
    );

    //QRコードを使うの処理
    const handleQRCodeUsage = useCallback(
        async (qrCodeData: string) => {
            const { spot_code: spotCode, app_id: appId } = queryString.parseUrl(qrCodeData).query;
            if (spotCode == null || spotCode.length !== OTP_INPUT_AMOUNT) {
                showNotificationDialog('error', '', t('notification.title'), t('error-message.spot-code.wrong-code'));
                setQrCodeData(null);
                return;
            }
            //TODO エラーメッセンジが変更する可能ので別のパタンで判別する
            if (appId !== APP_ID) {
                showNotificationDialog('error', '', t('notification.title'), t('error-message.spot-code.wrong-code'));
                setQrCodeData(null);
                return;
            }
            void closeCamera();
            await confirmSpotCodeAuthentication(spotCode as string);
        },
        [confirmSpotCodeAuthentication, t],
    );

    //QRコードの値が変わる時の処理
    useEffect(() => {
        if (qrCodeData) {
            void handleQRCodeUsage(qrCodeData);
        }
    }, [qrCodeData, handleQRCodeUsage]);

    /**
     * 戻るボタンの処理
     *
     * @returns {void}
     */
    const handleBackButton = () => {
        navigate('/bought');
    };

    //スポットコード入力タンを押下すの処理
    const handleInputCodeClick = useCallback(() => {
        setIsCodeInputModalOpen(true);
        setIsCameraOpen(false);
    }, []);

    // スポット識別コード入力モーダルを閉じた時
    const spotIdentificationCodeOnCancel = () => {
        setIsCodeInputModalOpen(false);
        setIsCameraOpen(true);
        setQrCodeData(null);
    };

    //QRコードをスキャンするの処理
    const handleScan = useCallback((data: string) => {
        if (data) {
            setQrCodeData(data);
        }
    }, []);

    //カメラを一時停止の処理
    const closeCamera = async () => {
        setIsCameraOpen(false);
    };

    return (
        <>
            {loading && <CenterLoading overFooter />}
            <SpotIdentificationCodeModal
                isOpen={isCodeInputModalOpen}
                onCancel={spotIdentificationCodeOnCancel}
                onSubmit={confirmSpotCodeAuthentication}
            />

            <div className={styles.invisibleHeader}>
                <div className={styles.backIconView} onClick={handleBackButton}>
                    {React.createElement(icBack, { className: styles.backIcon }) ?? 'Back'}
                </div>
            </div>
            {isCameraOpen && <QrCodeScan onScan={handleScan} />}

            <CustomAntButton type="outlined" className={styles.codeInputBtn} onClick={handleInputCodeClick}>
                {t('qr-code.enter-spot-code')}
            </CustomAntButton>
        </>
    );
});

export default CameraCodeScreen;
